import {
  createEffect,
  createMemo,
  createResource,
  createSignal,
  For,
  onCleanup,
  onMount,
  Show,
} from "solid-js";
import { useSearchParams } from "@solidjs/router";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import type {
  Cluster,
  ClusterMember,
  ClusterMemberResult,
  ClusterReport,
  ClusterOrder,
  FlexResource,
  OrderCheck,
} from "@energyplatform/client";
import { api, session } from "../services.ts";
import { access } from "../access.ts";
import { buildXlsx, type XlsxCell } from "../xlsx.ts";
import { locale, t } from "../i18n.ts";
import { formatDate, formatNumber, formatShare } from "../format.ts";
import { pathTo, siteTree } from "../context.ts";
import { Icon } from "../icons.tsx";
import { ErrorBox } from "../components/ErrorBox.tsx";
import { Chart, type ChartSeries } from "../components/Chart.tsx";
import {
  ConfirmDialog,
  Dialog,
  operator,
  PageHeader,
  Skeleton,
  Tabs,
  useAction,
} from "../components/ui.tsx";

const TABS = [
  "balance",
  "members",
  "sharing",
  "map",
  "member",
  "flexibility",
  "orders",
] as const;
type Tab = (typeof TABS)[number];
/** Shown greyed: the cluster platform's flexibility and orders are not computed here yet. */
const LATER: readonly Tab[] = [];

const PERIODS = ["yesterday", "week", "month", "prevMonth"] as const;
type Period = (typeof PERIODS)[number];
const METHODS = ["proRata", "static", "equal"] as const;
type Method = (typeof METHODS)[number];

const HOUR = 3_600_000;
const DAY = 24 * HOUR;

/** The period in local days, never past the last whole hour (an hour still running has no complete data). */
function periodOf(p: Period): { from: Date; to: Date } {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const hour = new Date();
  hour.setMinutes(0, 0, 0);
  const capped = (to: Date) => (to > hour ? hour : to);
  switch (p) {
    case "yesterday":
      return { from: new Date(today.getTime() - DAY), to: today };
    case "week":
      return { from: new Date(today.getTime() - 7 * DAY), to: capped(hour) };
    case "month":
      return {
        from: new Date(today.getFullYear(), today.getMonth(), 1),
        to: capped(hour),
      };
    case "prevMonth":
      return {
        from: new Date(today.getFullYear(), today.getMonth() - 1, 1),
        to: new Date(today.getFullYear(), today.getMonth(), 1),
      };
  }
}

/** Energy with as many decimals as its size needs: 1234 kWh, 56.7 kWh, 0.62 kWh. */
const kwh = (v: number | null | undefined, maxDigits = 2) => {
  if (v === null || v === undefined) {
    return "—";
  }
  const a = Math.abs(v);
  const digits = Math.min(maxDigits, a >= 100 ? 0 : a >= 10 ? 1 : 2);
  return `${formatNumber(v, locale(), digits)} kWh`;
};
const pln = (v: number | null | undefined) =>
  v === null || v === undefined ? "—" : `${formatNumber(v, locale(), 2)} zł`;

const placeName = (id: string) =>
  pathTo(siteTree()?.nodes ?? [], id)
    .map((n) => n.name)
    .join(" / ") || id;

/** Whether the signed-in user may change clusters and members (admin); the host checks every request anyway. */
const admin = () => {
  const s = session();
  return s.status === "signedIn" && s.user.roles.includes("admin");
};

/**
 * Energy cluster (prototype "Klaster"), computed on the platform's own data: members are places with a grid
 * connection meter; in every hour the cluster covers inside the smaller of what its members drew and fed. Shares by
 * three sharing methods are an estimate, not a settlement; values use the TGE day-ahead price.
 */
export function ClusterPage() {
  return (
    <Show when={access().unrestricted} fallback={<MyClusterShare />}>
      <OperatorCluster />
    </Show>
  );
}

function OperatorCluster() {
  const [params, setParams] = useSearchParams<{
    tab?: string;
    cluster?: string;
  }>();
  const tab = (): Tab =>
    TABS.includes(params.tab as Tab) ? (params.tab as Tab) : "balance";
  const [clusters, { refetch: refetchClusters }] = createResource(() =>
    api.clusters.list(),
  );
  const cluster = (): Cluster | undefined =>
    (clusters() ?? []).find((c) => c.id === params.cluster) ??
    (clusters() ?? [])[0];
  const [members, { refetch: refetchMembers }] = createResource(
    () => cluster()?.id,
    (id) => api.clusters.members(id),
  );
  const [period, setPeriod] = createSignal<Period>("month");
  const [report, { refetch: refetchReport }] = createResource(
    () => {
      const c = cluster();
      return c
        ? { id: c.id, ...periodOf(period()), n: members()?.length }
        : undefined;
    },
    (k) => api.clusters.report(k.id, k.from, k.to),
  );
  const [editing, setEditing] = createSignal<{ cluster?: Cluster }>();
  const reload = () => {
    api.invalidate("/api/v1/clusters");
    void refetchClusters();
    void refetchMembers();
    void refetchReport();
  };

  return (
    <>
      <PageHeader
        eyebrow={`${t("cluster.eyebrow")}${cluster() ? ` · ${cluster()!.name}` : ""}`}
        title={t(`cluster.title.${tab()}`)}
        sub={t(`cluster.intro.${tab()}`)}
      >
        <Show when={(clusters() ?? []).length > 1}>
          <select
            aria-label={t("cluster.choose")}
            value={cluster()?.id ?? ""}
            onChange={(e) => setParams({ cluster: e.currentTarget.value })}
          >
            <For each={clusters()}>
              {(c) => <option value={c.id}>{c.name}</option>}
            </For>
          </select>
        </Show>
        <Show when={admin()}>
          <Show when={cluster()}>
            <button
              type="button"
              class="btn"
              onClick={() => setEditing({ cluster: cluster() })}
            >
              <Icon name="edit" />
              {t("cluster.rename")}
            </button>
          </Show>
          <button type="button" class="btn" onClick={() => setEditing({})}>
            <Icon name="plus" />
            {t("cluster.new")}
          </button>
        </Show>
      </PageHeader>
      <Show
        when={!clusters.error}
        fallback={<ErrorBox error={clusters.error} />}
      >
        <Show when={clusters()} fallback={<Skeleton lines={5} />}>
          <Show
            when={cluster()}
            fallback={
              <section class="card">
                <div class="card-b hint">
                  {t(admin() ? "cluster.noneAdmin" : "cluster.none")}
                </div>
              </section>
            }
          >
            {(c) => (
              <>
                <Tabs
                  tabs={TABS}
                  value={tab()}
                  onChange={(x) => setParams({ tab: x })}
                  label={(x) =>
                    LATER.includes(x)
                      ? `${t(`cluster.tab.${x}`)} · ${t("cluster.soon")}`
                      : t(`cluster.tab.${x}`)
                  }
                />
                <Show when={!LATER.includes(tab()) && tab() !== "members"}>
                  <div
                    class="seg"
                    role="group"
                    aria-label={t("cluster.period")}
                    style={{ "margin-bottom": "12px" }}
                  >
                    <For each={PERIODS}>
                      {(p) => (
                        <button
                          type="button"
                          aria-pressed={period() === p}
                          onClick={() => setPeriod(p)}
                        >
                          {t(`cluster.periods.${p}`)}
                        </button>
                      )}
                    </For>
                  </div>
                </Show>
                <Show when={report.error && !LATER.includes(tab())}>
                  <ErrorBox error={report.error} />
                </Show>
                <Show when={tab() === "balance"}>
                  <Balance report={report()} />
                </Show>
                <Show when={tab() === "members"}>
                  <Members
                    cluster={c()}
                    members={members() ?? []}
                    report={report()}
                    onChanged={reload}
                  />
                </Show>
                <Show when={tab() === "sharing"}>
                  <Sharing report={report()} name={c().name} />
                </Show>
                <Show when={tab() === "map"}>
                  <Show when={report()} fallback={<Skeleton lines={6} />}>
                    {(r) => <MembersMap report={r()} />}
                  </Show>
                </Show>
                <Show when={tab() === "member"}>
                  <MemberShare report={report()} />
                </Show>
                <Show when={tab() === "flexibility"}>
                  <Flexibility cluster={c()} />
                </Show>
                <Show when={tab() === "orders"}>
                  <Orders cluster={c()} />
                </Show>
              </>
            )}
          </Show>
        </Show>
      </Show>
      <Show when={editing()}>
        {(e) => (
          <ClusterDialog
            cluster={e().cluster}
            onClose={() => setEditing(undefined)}
            onSaved={(saved) => {
              reload();
              setParams({ cluster: saved.id });
            }}
          />
        )}
      </Show>
    </>
  );
}

function Kpi(props: { label: string; value: string; sub?: string }) {
  return (
    <section class="card kpi">
      <span class="lbl">{props.label}</span>
      <span class="val">{props.value}</span>
      <Show when={props.sub}>
        <span class="q">{props.sub}</span>
      </Show>
    </section>
  );
}

/** Completeness of the period: a missing reading of any member leaves the hour out of the balance. */
function Completeness(props: { report: ClusterReport }) {
  return (
    <Show
      when={
        props.report.members.length > 0 &&
        props.report.completeHours < props.report.hours
      }
    >
      <div class="note" style={{ "margin-bottom": "12px" }}>
        <Icon name="alert" />
        <span>
          {t("cluster.incomplete", {
            complete: props.report.completeHours,
            hours: props.report.hours,
          })}
        </span>
      </div>
    </Show>
  );
}

function Balance(props: { report: ClusterReport | undefined }) {
  return (
    <Show when={props.report} fallback={<Skeleton lines={6} />}>
      {(r) => {
        const bucket = () => (r().resolution === "hour" ? HOUR : DAY);
        const series = createMemo<ChartSeries[]>(() => [
          {
            key: "import",
            label: t("cluster.import"),
            color: "var(--m-el)",
            kind: "line",
            points: r().buckets.map((b) => ({
              t: new Date(b.at).getTime() + bucket() / 2,
              v: b.import,
              c: b.coverage,
            })),
          },
          {
            key: "export",
            label: t("cluster.export"),
            color: "var(--m-pv)",
            kind: "line",
            points: r().buckets.map((b) => ({
              t: new Date(b.at).getTime() + bucket() / 2,
              v: b.export,
              c: b.coverage,
            })),
          },
          {
            key: "shared",
            label: t("cluster.shared"),
            color: "var(--ok)",
            kind: "bar",
            points: r().buckets.map((b) => ({
              t: new Date(b.at).getTime(),
              v: b.shared,
              c: b.coverage,
            })),
          },
        ]);
        const gaps = () =>
          r()
            .buckets.filter((b) => b.coverage === 0)
            .map((b) => ({
              from: new Date(b.at).getTime(),
              to: new Date(b.at).getTime() + bucket(),
            }));
        const first = () => r().buckets[0];
        const last = () => r().buckets[r().buckets.length - 1];
        return (
          <>
            <Completeness report={r()} />
            <div class="grid g-kpi">
              <Kpi
                label={t("cluster.kpi.members")}
                value={String(r().members.length)}
                sub={t("cluster.kpi.membersSub", {
                  producers: r().members.filter((m) =>
                    m.roles.includes("producer"),
                  ).length,
                })}
              />
              <Kpi
                label={t("cluster.kpi.shared")}
                value={kwh(r().shared)}
                sub={t("cluster.kpi.sharedSub", {
                  imported: kwh(r().import),
                  exported: kwh(r().export),
                })}
              />
              <Kpi
                label={t("cluster.kpi.selfSufficiency")}
                value={formatShare(r().selfSufficiency, locale())}
                sub={t("cluster.kpi.selfSufficiencySub")}
              />
              <Kpi
                label={t("cluster.kpi.selfConsumption")}
                value={formatShare(r().selfConsumption, locale())}
                sub={t("cluster.kpi.selfConsumptionSub")}
              />
              <Kpi
                label={t("cluster.kpi.value")}
                value={pln(r().sharedValue)}
                sub={t("cluster.kpi.valueSub")}
              />
            </div>
            <section class="card">
              <div class="card-h">
                <h2>{t("cluster.chart")}</h2>
                <span class="meta">{t("cluster.chartMeta")}</span>
              </div>
              <div class="card-b">
                <Show when={first() && last()}>
                  <Chart
                    series={series()}
                    from={new Date(first()!.at)}
                    to={new Date(new Date(last()!.at).getTime() + bucket())}
                    unit="kWh"
                    locale={locale()}
                    bucketMs={bucket()}
                    gaps={gaps()}
                    gapLabel={t("cluster.gap")}
                    emptyText={t("cluster.noData")}
                    height={240}
                  />
                </Show>
                <p class="hint" style={{ margin: "8px 0 0" }}>
                  {t("cluster.logical")}
                </p>
              </div>
            </section>
          </>
        );
      }}
    </Show>
  );
}

function RoleChips(props: { roles: string[] }) {
  return (
    <For each={props.roles}>
      {(x) => (
        <span
          class={`chip ${x === "producer" ? "ok" : x === "storage" ? "acc" : ""}`}
          style={{ "margin-right": "4px" }}
        >
          {t(`cluster.role.${x}`)}
        </span>
      )}
    </For>
  );
}

function Members(props: {
  cluster: Cluster;
  members: ClusterMember[];
  report: ClusterReport | undefined;
  onChanged: () => void;
}) {
  const [editing, setEditing] = createSignal<{ member?: ClusterMember }>();
  const [removing, setRemoving] = createSignal<ClusterMember>();
  const result = (id: string) =>
    props.report?.members.find((m) => m.memberId === id);
  return (
    <>
      <section class="card">
        <div class="card-h">
          <h2>{t("cluster.membersTitle")}</h2>
          <span class="meta">
            {t("cluster.membersMeta", { count: props.members.length })}
          </span>
          <Show when={admin()}>
            <button
              type="button"
              class="btn sm"
              style={{ "margin-left": "auto" }}
              onClick={() => setEditing({})}
            >
              <Icon name="plus" />
              {t("cluster.addMember")}
            </button>
          </Show>
        </div>
        <Show
          when={props.members.length > 0}
          fallback={<div class="card-b hint">{t("cluster.noMembers")}</div>}
        >
          <div class="scrollx">
            <table class="tbl">
              <thead>
                <tr>
                  <th>{t("cluster.member")}</th>
                  <th>{t("cluster.roles")}</th>
                  <th>{t("cluster.membership")}</th>
                  <th class="r">{t("cluster.weight")}</th>
                  <th class="r">{t("cluster.import")}</th>
                  <th class="r">{t("cluster.export")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                <For each={props.members}>
                  {(m) => (
                    <tr>
                      <td>
                        <b style={{ "font-weight": 500 }}>
                          {placeName(m.nodeId)}
                        </b>
                      </td>
                      <td>
                        <RoleChips roles={result(m.id)?.roles ?? []} />
                      </td>
                      <td class="hint">
                        {m.leftOn
                          ? t("cluster.between", {
                              from: formatDate(m.joinedOn, locale()),
                              to: formatDate(m.leftOn, locale()),
                            })
                          : t("cluster.since", {
                              from: formatDate(m.joinedOn, locale()),
                            })}
                      </td>
                      <td class="r num">
                        {formatNumber(m.weight, locale(), 2)}
                      </td>
                      <td class="r num">{kwh(result(m.id)?.import)}</td>
                      <td class="r num">{kwh(result(m.id)?.export)}</td>
                      <td class="r">
                        <Show when={admin()}>
                          <button
                            type="button"
                            class="btn ghost sm"
                            title={t("common.edit")}
                            aria-label={t("common.edit")}
                            onClick={() => setEditing({ member: m })}
                          >
                            <Icon name="edit" />
                          </button>
                          <button
                            type="button"
                            class="btn ghost sm"
                            title={t("common.delete")}
                            aria-label={t("common.delete")}
                            onClick={() => setRemoving(m)}
                          >
                            <Icon name="x" />
                          </button>
                        </Show>
                      </td>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </div>
        </Show>
        <div class="card-b hint">{t("cluster.membersHint")}</div>
      </section>
      <Show when={editing()}>
        {(e) => (
          <MemberDialog
            cluster={props.cluster}
            member={e().member}
            taken={props.members.map((m) => m.nodeId)}
            onClose={() => setEditing(undefined)}
            onSaved={props.onChanged}
          />
        )}
      </Show>
      <Show when={removing()}>
        {(m) => (
          <ConfirmDialog
            title={t("cluster.removeTitle", { name: placeName(m().nodeId) })}
            text={t("cluster.removeText")}
            confirm={t("common.delete")}
            danger
            onConfirm={async () => {
              await api.clusters.removeMember(props.cluster.id, m().id);
              props.onChanged();
            }}
            onClose={() => setRemoving(undefined)}
          />
        )}
      </Show>
    </>
  );
}

function Sharing(props: { report: ClusterReport | undefined; name: string }) {
  const [method, setMethod] = createSignal<Method>("proRata");
  return (
    <Show when={props.report} fallback={<Skeleton lines={6} />}>
      {(r) => {
        const rows = () =>
          [...r().members].sort(
            (a, b) => (b.received[method()] ?? 0) - (a.received[method()] ?? 0),
          );
        return (
          <>
            <Completeness report={r()} />
            <section class="card">
              <div class="card-h">
                <h2>{t("cluster.sharingTitle")}</h2>
                <span class="meta">
                  {t("cluster.sharingMeta", { shared: kwh(r().shared) })}
                </span>
              </div>
              <div class="card-b">
                <div class="seg" role="group" aria-label={t("cluster.method")}>
                  <For each={METHODS}>
                    {(m) => (
                      <button
                        type="button"
                        aria-pressed={method() === m}
                        onClick={() => setMethod(m)}
                      >
                        {t(`cluster.methods.${m}`)}
                      </button>
                    )}
                  </For>
                </div>
                <p class="hint" style={{ margin: "8px 0 0" }}>
                  {t(`cluster.methodHint.${method()}`)}
                </p>
              </div>
              <div class="scrollx">
                <table class="tbl">
                  <thead>
                    <tr>
                      <th>{t("cluster.member")}</th>
                      <th class="r">{t("cluster.received")}</th>
                      <th class="r">{t("cluster.coverage")}</th>
                      <th class="r">{t("cluster.contributed")}</th>
                      <th class="r">{t("cluster.receivedValue")}</th>
                      <th class="r">{t("cluster.vsProRata")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <For each={rows()}>
                      {(m) => {
                        const got = () => m.received[method()] ?? 0;
                        const diff = () => got() - (m.received.proRata ?? 0);
                        return (
                          <tr>
                            <td>
                              <b style={{ "font-weight": 500 }}>{m.name}</b>
                              <div>
                                <RoleChips roles={m.roles} />
                              </div>
                            </td>
                            <td class="r num">{kwh(got())}</td>
                            <td class="r num">
                              {m.import > 0
                                ? formatShare(got() / m.import, locale())
                                : "—"}
                            </td>
                            <td class="r num">{kwh(m.contributed)}</td>
                            <td class="r num">
                              {pln(m.receivedValue[method()])}
                            </td>
                            <td class="r num">
                              {method() === "proRata" || Math.abs(diff()) < 0.05
                                ? "—"
                                : `${diff() > 0 ? "+" : "−"}${kwh(Math.abs(diff()))}`}
                            </td>
                          </tr>
                        );
                      }}
                    </For>
                  </tbody>
                </table>
              </div>
              <div class="card-b hint">{t("cluster.estimate")}</div>
            </section>
            <section class="card">
              <div class="card-h">
                <h2>{t("cluster.exportTitle")}</h2>
                <button
                  type="button"
                  class="btn sm"
                  style={{ "margin-left": "auto" }}
                  onClick={() => exportBalance(r(), props.name)}
                >
                  <Icon name="file" />
                  {t("cluster.exportButton")}
                </button>
              </div>
              <div class="card-b hint">{t("cluster.exportHint")}</div>
            </section>
          </>
        );
      }}
    </Show>
  );
}

// Leaflet sets SVG attributes, where CSS variables do not apply: the theme's colours are read once.
const color = (name: string) =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim() ||
  "#888";

function MembersMap(props: { report: ClusterReport }) {
  const placed = () =>
    props.report.members.filter(
      (m) => m.latitude !== null && m.longitude !== null,
    );
  let host!: HTMLDivElement;
  let map: L.Map | undefined;
  let layer: L.LayerGroup | undefined;
  onMount(() => {
    map = L.map(host, { scrollWheelZoom: false, attributionControl: true });
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      className: "street-tiles",
      maxZoom: 18,
      attribution: "© OpenStreetMap",
    }).addTo(map);
    layer = L.layerGroup().addTo(map);
    onCleanup(() => map?.remove());
  });
  createEffect(() => {
    const all = placed();
    if (!map || !layer) {
      return;
    }
    layer.clearLayers();
    if (all.length === 0) {
      map.setView([52, 19], 6);
      return;
    }
    const bounds: L.LatLngTuple[] = [];
    for (const m of all) {
      const at: L.LatLngTuple = [m.latitude!, m.longitude!];
      bounds.push(at);
      const producer = m.roles.includes("producer");
      L.circleMarker(at, {
        radius: Math.max(7, Math.min(18, Math.sqrt(m.import + m.export) / 3)),
        color: "#fff",
        weight: 2,
        fillColor: color(producer ? "--m-pv" : "--m-el"),
        fillOpacity: 0.9,
      })
        .bindTooltip(
          `${m.name} · ${m.roles.map((x) => t(`cluster.role.${x}`)).join(", ")}`,
        )
        .addTo(layer);
    }
    if (bounds.length === 1) {
      map.setView(bounds[0]!, 15);
    } else {
      map.fitBounds(L.latLngBounds(bounds), { padding: [30, 30], maxZoom: 16 });
    }
  });
  const unplaced = () =>
    props.report.members.filter(
      (m) => m.latitude === null || m.longitude === null,
    );
  return (
    <section class="card">
      <div class="card-h">
        <h2>{t("cluster.mapTitle")}</h2>
        <span class="meta">{t("cluster.mapMeta")}</span>
      </div>
      <div class="mapgrid">
        <div style={{ padding: "10px" }}>
          <div
            ref={host}
            class="map"
            style={{ height: "420px" }}
            role="region"
            aria-label={t("cluster.mapTitle")}
          />
        </div>
        <div class="side">
          <table class="tbl">
            <tbody>
              <For each={props.report.members}>
                {(m) => (
                  <tr>
                    <td>
                      <b style={{ "font-weight": 500 }}>{m.name}</b>
                      <div>
                        <RoleChips roles={m.roles} />
                      </div>
                    </td>
                    <td class="r num">
                      {kwh(m.export > m.import ? -m.export : m.import, 0)}
                    </td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
          <Show when={unplaced().length > 0}>
            <p class="hint" style={{ padding: "0 10px" }}>
              {t("cluster.unplaced", { count: unplaced().length })}
            </p>
          </Show>
        </div>
      </div>
    </section>
  );
}

function MemberShare(props: { report: ClusterReport | undefined }) {
  const [chosen, setChosen] = createSignal<string>();
  return (
    <Show when={props.report} fallback={<Skeleton lines={6} />}>
      {(r) => {
        const m = (): ClusterMemberResult | undefined =>
          r().members.find((x) => x.memberId === chosen()) ?? r().members[0];
        return (
          <Show
            when={m()}
            fallback={
              <div class="card card-b hint">{t("cluster.noMembers")}</div>
            }
          >
            {(p) => (
              <>
                <div
                  class="field"
                  style={{ "max-width": "360px", "margin-bottom": "12px" }}
                >
                  <label for="clMember">{t("cluster.viewAs")}</label>
                  <select
                    id="clMember"
                    value={p().memberId}
                    onChange={(e) => setChosen(e.currentTarget.value)}
                  >
                    <For each={r().members}>
                      {(x) => <option value={x.memberId}>{x.name}</option>}
                    </For>
                  </select>
                </div>
                <Completeness report={r()} />
                <div class="grid g-kpi">
                  <Kpi
                    label={t("cluster.my.balance")}
                    value={kwh(p().import - p().export, 0)}
                    sub={t("cluster.my.balanceSub", {
                      imported: kwh(p().import, 0),
                      exported: kwh(p().export, 0),
                    })}
                  />
                  <Kpi
                    label={t("cluster.my.received")}
                    value={kwh(p().received.proRata, 0)}
                    sub={t("cluster.my.receivedSub", {
                      share:
                        p().import > 0
                          ? formatShare(
                              (p().received.proRata ?? 0) / p().import,
                              locale(),
                            )
                          : "—",
                    })}
                  />
                  <Kpi
                    label={t("cluster.my.contributed")}
                    value={kwh(p().contributed, 0)}
                    sub={t("cluster.my.contributedSub", {
                      share:
                        p().export > 0
                          ? formatShare(p().contributed / p().export, locale())
                          : "—",
                    })}
                  />
                  <Kpi
                    label={t("cluster.my.value")}
                    value={pln(p().receivedValue.proRata)}
                    sub={t("cluster.kpi.valueSub")}
                  />
                </div>
                <div class="grid g-2">
                  <section class="card">
                    <div class="card-h">
                      <h2>{t("cluster.my.clusterTitle")}</h2>
                      <span class="meta">{t("cluster.my.clusterMeta")}</span>
                    </div>
                    <div class="card-b">
                      <dl class="kv" style={{ "margin-top": 0 }}>
                        <dt>{t("cluster.kpi.members")}</dt>
                        <dd>{r().members.length}</dd>
                        <dt>{t("cluster.kpi.shared")}</dt>
                        <dd class="num">{kwh(r().shared)}</dd>
                        <dt>{t("cluster.kpi.selfSufficiency")}</dt>
                        <dd class="num">
                          {formatShare(r().selfSufficiency, locale())}
                        </dd>
                        <dt>{t("cluster.kpi.selfConsumption")}</dt>
                        <dd class="num">
                          {formatShare(r().selfConsumption, locale())}
                        </dd>
                      </dl>
                    </div>
                  </section>
                  <section class="card" style={{ opacity: 0.6 }}>
                    <div class="card-h">
                      <h2>{t("cluster.my.sharingTitle")}</h2>
                      <span class="chip">{t("cluster.soon")}</span>
                    </div>
                    <div class="card-b hint">{t("cluster.my.sharingHint")}</div>
                  </section>
                </div>
              </>
            )}
          </Show>
        );
      }}
    </Show>
  );
}

/** Members' resources the operator may act on: switches and EV chargers in member places, with state and power now. */
function Flexibility(props: { cluster: Cluster }) {
  const [resources] = createResource(
    () => props.cluster.id,
    (id) => api.clusters.flexibility(id),
  );
  const total = () =>
    (resources() ?? []).reduce((s, r) => s + Math.max(0, r.powerKw ?? 0), 0);
  return (
    <section class="card">
      <div class="card-h">
        <h2>{t("cluster.flex.title")}</h2>
        <span class="meta">
          {t("cluster.flex.meta", {
            count: (resources() ?? []).length,
            kw: num1(total()),
          })}
        </span>
      </div>
      <Show
        when={!resources.error}
        fallback={<ErrorBox error={resources.error} />}
      >
        <Show when={resources()} fallback={<Skeleton lines={4} />}>
          {(list) => (
            <Show
              when={list().length > 0}
              fallback={<div class="card-b hint">{t("cluster.flex.none")}</div>}
            >
              <div class="scrollx">
                <table class="tbl">
                  <thead>
                    <tr>
                      <th>{t("cluster.member")}</th>
                      <th>{t("cluster.flex.resource")}</th>
                      <th>{t("cluster.flex.kindLabel")}</th>
                      <th>{t("cluster.flex.state")}</th>
                      <th class="r">{t("cluster.flex.power")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <For each={list()}>
                      {(r) => (
                        <tr>
                          <td>{r.memberName}</td>
                          <td>
                            <b style={{ "font-weight": 500 }}>{r.deviceName}</b>
                          </td>
                          <td>{t(`cluster.flex.kinds.${r.kind}`)}</td>
                          <td>
                            <span class="chip">{r.state ?? "—"}</span>
                          </td>
                          <td class="r num">
                            {r.powerKw == null ? "—" : `${num1(r.powerKw)} kW`}
                          </td>
                        </tr>
                      )}
                    </For>
                  </tbody>
                </table>
              </div>
            </Show>
          )}
        </Show>
      </Show>
      <div class="card-b hint">{t("cluster.flex.hint")}</div>
    </section>
  );
}

const num1 = (v: number) => formatNumber(v, locale(), 1);

const localInput = (d: Date) => {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

/** Orders on members' resources: a window, a goal and a target; started and ended by the platform, checked by measurement. */
function Orders(props: { cluster: Cluster }) {
  const [orders, { refetch }] = createResource(
    () => props.cluster.id,
    (id) => api.clusters.orders(id),
  );
  const [resources] = createResource(
    () => props.cluster.id,
    (id) => api.clusters.flexibility(id),
  );
  const [creating, setCreating] = createSignal(false);
  const [checks, setChecks] = createSignal<Record<string, OrderCheck>>({});
  const name = (deviceId: string) =>
    (resources() ?? []).find((r) => r.deviceId === deviceId)?.deviceName ??
    deviceId;
  const check = async (o: ClusterOrder) => {
    const c = await api.clusters.checkOrder(props.cluster.id, o.id);
    setChecks({ ...checks(), [o.id]: c });
  };
  const time = (iso: string) =>
    new Date(iso).toLocaleString(locale(), {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  return (
    <>
      <section class="card">
        <div class="card-h">
          <h2>{t("cluster.orders.title")}</h2>
          <Show when={operator()}>
            <button
              type="button"
              class="btn sm primary"
              style={{ "margin-left": "auto" }}
              onClick={() => setCreating(true)}
            >
              <Icon name="plus" />
              {t("cluster.orders.new")}
            </button>
          </Show>
        </div>
        <Show when={!orders.error} fallback={<ErrorBox error={orders.error} />}>
          <Show
            when={(orders() ?? []).length > 0}
            fallback={
              <div class="card-b hint">
                {orders.loading ? "…" : t("cluster.orders.none")}
              </div>
            }
          >
            <div class="scrollx">
              <table class="tbl">
                <thead>
                  <tr>
                    <th>{t("cluster.orders.window")}</th>
                    <th>{t("cluster.orders.goal")}</th>
                    <th class="r">{t("cluster.orders.target")}</th>
                    <th>{t("cluster.orders.resources")}</th>
                    <th>{t("cluster.orders.state")}</th>
                    <th>{t("cluster.orders.execution")}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  <For each={orders()}>
                    {(o) => (
                      <tr>
                        <td class="num" style={{ "white-space": "nowrap" }}>
                          {time(o.start)} – {time(o.end)}
                        </td>
                        <td>{t(`cluster.orders.goals.${o.goal}`)}</td>
                        <td class="r num">{num1(o.targetKw)} kW</td>
                        <td class="hint">
                          {o.deviceIds.map(name).join(", ")}
                          <For each={o.actions}>
                            {(a) => (
                              <div>
                                {time(a.at)} · {name(a.deviceId)} → {a.command}{" "}
                                · {t(`automations.outcome.${a.outcome}`)}
                              </div>
                            )}
                          </For>
                        </td>
                        <td>
                          <span
                            class={`chip ${o.state === "active" ? "ok" : o.state === "planned" ? "acc" : ""}`}
                          >
                            {t(`cluster.orders.states.${o.state}`)}
                          </span>{" "}
                          <span class="chip">
                            {t(`automations.mode.${o.mode}`)}
                          </span>
                        </td>
                        <td>
                          <Show
                            when={checks()[o.id]}
                            fallback={
                              <Show when={o.state !== "planned"}>
                                <button
                                  type="button"
                                  class="btn ghost sm"
                                  onClick={() => void check(o)}
                                >
                                  {t("cluster.orders.check")}
                                </button>
                              </Show>
                            }
                          >
                            {(c) => (
                              <span
                                class={`chip ${c().met === true ? "ok" : c().met === false ? "crit" : ""}`}
                                title={t("cluster.orders.checkHint", {
                                  baseline:
                                    c().baselineKw == null
                                      ? "—"
                                      : num1(c().baselineKw!),
                                  window:
                                    c().windowKw == null
                                      ? "—"
                                      : num1(c().windowKw!),
                                })}
                              >
                                {c().changeKw == null
                                  ? t("cluster.orders.noData")
                                  : t("cluster.orders.changed", {
                                      kw: `${c().changeKw! > 0 ? "+" : "−"}${num1(Math.abs(c().changeKw!))}`,
                                    })}
                              </span>
                            )}
                          </Show>
                        </td>
                        <td class="r">
                          <Show
                            when={
                              operator() &&
                              (o.state === "planned" || o.state === "active")
                            }
                          >
                            <button
                              type="button"
                              class="btn ghost sm"
                              onClick={async () => {
                                await api.clusters.cancelOrder(
                                  props.cluster.id,
                                  o.id,
                                );
                                api.invalidate("/api/v1/clusters");
                                void refetch();
                              }}
                            >
                              {t("cluster.orders.cancel")}
                            </button>
                          </Show>
                        </td>
                      </tr>
                    )}
                  </For>
                </tbody>
              </table>
            </div>
          </Show>
        </Show>
        <div class="card-b hint">{t("cluster.orders.hint")}</div>
      </section>
      <Show when={creating()}>
        <OrderDialog
          cluster={props.cluster}
          resources={resources() ?? []}
          onClose={() => setCreating(false)}
          onSaved={() => {
            api.invalidate("/api/v1/clusters");
            void refetch();
          }}
        />
      </Show>
    </>
  );
}

function OrderDialog(props: {
  cluster: Cluster;
  resources: FlexResource[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const start0 = new Date(Date.now() + 5 * 60_000);
  start0.setSeconds(0, 0);
  const [start, setStart] = createSignal(localInput(start0));
  const [end, setEnd] = createSignal(
    localInput(new Date(start0.getTime() + 3_600_000)),
  );
  const [goal, setGoal] = createSignal<"reduce" | "increase">("reduce");
  const [target, setTarget] = createSignal("1");
  const [mode, setMode] = createSignal<"test" | "live">("test");
  const [chosen, setChosen] = createSignal<string[]>([]);
  const [run, error, busy] = useAction();
  const toggle = (id: string, on: boolean) =>
    setChosen(on ? [...chosen(), id] : chosen().filter((x) => x !== id));
  return (
    <Dialog
      title={t("cluster.orders.new")}
      wide
      onClose={props.onClose}
      footer={
        <>
          <button type="button" class="btn" onClick={() => props.onClose()}>
            {t("common.cancel")}
          </button>
          <button
            type="button"
            class="btn primary"
            disabled={busy() || chosen().length === 0}
            onClick={() =>
              void run(async () => {
                await api.clusters.createOrder(props.cluster.id, {
                  start: new Date(start()).toISOString(),
                  end: new Date(end()).toISOString(),
                  goal: goal(),
                  targetKw: Number(target().replace(",", ".")),
                  deviceIds: chosen(),
                  mode: mode(),
                });
                props.onSaved();
                props.onClose();
              })
            }
          >
            {t("common.save")}
          </button>
        </>
      }
    >
      <div style={{ display: "grid", gap: "10px" }}>
        <div class="seg" role="group" aria-label={t("cluster.orders.goal")}>
          <For each={["reduce", "increase"] as const}>
            {(g) => (
              <button
                type="button"
                aria-pressed={goal() === g}
                onClick={() => setGoal(g)}
              >
                {t(`cluster.orders.goals.${g}`)}
              </button>
            )}
          </For>
        </div>
        <p class="hint" style={{ margin: 0 }}>
          {t(`cluster.orders.goalHint.${goal()}`)}
        </p>
        <div class="grid g-2e" style={{ gap: "10px" }}>
          <div class="field">
            <label for="orStart">{t("cluster.orders.start")}</label>
            <input
              id="orStart"
              type="datetime-local"
              value={start()}
              onInput={(e) => setStart(e.currentTarget.value)}
            />
          </div>
          <div class="field">
            <label for="orEnd">{t("cluster.orders.end")}</label>
            <input
              id="orEnd"
              type="datetime-local"
              value={end()}
              onInput={(e) => setEnd(e.currentTarget.value)}
            />
          </div>
          <div class="field">
            <label for="orTarget">{t("cluster.orders.targetLabel")}</label>
            <input
              id="orTarget"
              inputmode="decimal"
              value={target()}
              onInput={(e) => setTarget(e.currentTarget.value)}
            />
          </div>
        </div>
        <div class="field">
          <span class="eyebrow">{t("cluster.orders.resources")}</span>
          <Show
            when={props.resources.length > 0}
            fallback={<span class="hint">{t("cluster.flex.none")}</span>}
          >
            <For each={props.resources}>
              {(r) => (
                <label
                  style={{
                    display: "flex",
                    gap: "6px",
                    "align-items": "center",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={chosen().includes(r.deviceId)}
                    onChange={(e) =>
                      toggle(r.deviceId, e.currentTarget.checked)
                    }
                  />
                  {r.memberName} · {r.deviceName}
                  <span class="hint">
                    ({t(`cluster.flex.kinds.${r.kind}`)}
                    {r.powerKw == null ? "" : `, ${num1(r.powerKw)} kW`})
                  </span>
                </label>
              )}
            </For>
          </Show>
        </div>
        <div class="field">
          <span class="eyebrow">{t("automations.modeLabel")}</span>
          <div class="seg" role="group" aria-label={t("automations.modeLabel")}>
            <For each={["test", "live"] as const}>
              {(m) => (
                <button
                  type="button"
                  aria-pressed={mode() === m}
                  onClick={() => setMode(m)}
                >
                  {t(`automations.mode.${m}`)}
                </button>
              )}
            </For>
          </div>
          <span class={mode() === "live" ? "chip warn" : "hint"}>
            {t(`cluster.orders.modeHint.${mode()}`)}
          </span>
        </div>
        <Show when={error()}>
          <ErrorBox error={error()} />
        </Show>
      </div>
    </Dialog>
  );
}

/** The balance per member and the cluster's hours, as a spreadsheet for the coordinator's settlement system. */
function exportBalance(report: ClusterReport, name: string) {
  const methods = ["proRata", "static", "equal"] as const;
  const members: XlsxCell[][] = [
    [
      { bold: t("cluster.member") },
      { bold: t("cluster.weight") },
      { bold: `${t("cluster.import")} (kWh)` },
      { bold: `${t("cluster.export")} (kWh)` },
      { bold: `${t("cluster.contributed")} (kWh)` },
      ...methods.map((m) => ({
        bold: `${t("cluster.received")} · ${t(`cluster.methods.${m}`)} (kWh)`,
      })),
      ...methods.map((m) => ({
        bold: `${t("cluster.receivedValue")} · ${t(`cluster.methods.${m}`)} (zł)`,
      })),
    ],
    ...report.members.map((m) => [
      m.name,
      m.weight,
      m.import,
      m.export,
      m.contributed,
      ...methods.map((x) => m.received[x] ?? 0),
      ...methods.map((x) => m.receivedValue[x] ?? null),
    ]),
  ];
  const hours: XlsxCell[][] = [
    [
      { bold: t("cluster.xlsx.time") },
      { bold: `${t("cluster.import")} (kWh)` },
      { bold: `${t("cluster.export")} (kWh)` },
      { bold: `${t("cluster.shared")} (kWh)` },
    ],
    ...report.buckets.map((b) => [
      new Date(b.at).toLocaleString(locale()),
      b.import,
      b.export,
      b.shared,
    ]),
  ];
  const blob = new Blob(
    [
      buildXlsx([
        {
          name: t("cluster.xlsx.members"),
          rows: members,
          widths: [28, 8, 14, 14, 14, 18, 18, 18, 16, 16, 16],
        },
        {
          name: t("cluster.xlsx.hours"),
          rows: hours,
          widths: [20, 14, 14, 16],
        },
      ]),
    ],
    {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    },
  );
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `${name.replace(/[\\/:*?"<>|]/g, "")} ${report.buckets[0]?.at.slice(0, 10) ?? ""}.xlsx`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

/**
 * A member's own view (prototype "Udział w klastrze") for a person who reaches only some places: for each of their
 * member places, its balance, energy from and to the cluster, and the cluster's totals — no other member's data.
 */
function MyClusterShare() {
  const [period, setPeriod] = createSignal<Period>("month");
  const [views] = createResource(
    () => periodOf(period()),
    (p) => api.clusters.mine(p.from, p.to),
  );
  return (
    <>
      <PageHeader
        eyebrow={t("cluster.eyebrow")}
        title={t("cluster.title.member")}
        sub={t("cluster.mine.intro")}
      />
      <div
        class="seg"
        role="group"
        aria-label={t("cluster.period")}
        style={{ "margin-bottom": "12px" }}
      >
        <For each={PERIODS}>
          {(p) => (
            <button
              type="button"
              aria-pressed={period() === p}
              onClick={() => setPeriod(p)}
            >
              {t(`cluster.periods.${p}`)}
            </button>
          )}
        </For>
      </div>
      <Show when={!views.error} fallback={<ErrorBox error={views.error} />}>
        <Show when={views()} fallback={<Skeleton lines={5} />}>
          {(list) => (
            <Show
              when={list().length > 0}
              fallback={
                <section class="card">
                  <div class="card-b hint">{t("cluster.mine.none")}</div>
                </section>
              }
            >
              <For each={list()}>
                {(v) => (
                  <>
                    <h2 style={{ margin: "8px 0" }}>
                      {v.member.name} · {v.clusterName}
                    </h2>
                    <div class="grid g-kpi">
                      <Kpi
                        label={t("cluster.my.balance")}
                        value={kwh(v.member.import - v.member.export)}
                        sub={t("cluster.my.balanceSub", {
                          imported: kwh(v.member.import),
                          exported: kwh(v.member.export),
                        })}
                      />
                      <Kpi
                        label={t("cluster.my.received")}
                        value={kwh(v.member.received.proRata)}
                        sub={t("cluster.my.receivedSub", {
                          share:
                            v.member.import > 0
                              ? formatShare(
                                  (v.member.received.proRata ?? 0) /
                                    v.member.import,
                                  locale(),
                                )
                              : "—",
                        })}
                      />
                      <Kpi
                        label={t("cluster.my.contributed")}
                        value={kwh(v.member.contributed)}
                        sub={t("cluster.my.contributedSub", {
                          share:
                            v.member.export > 0
                              ? formatShare(
                                  v.member.contributed / v.member.export,
                                  locale(),
                                )
                              : "—",
                        })}
                      />
                      <Kpi
                        label={t("cluster.my.value")}
                        value={pln(v.member.receivedValue.proRata)}
                        sub={t("cluster.kpi.valueSub")}
                      />
                    </div>
                    <section class="card">
                      <div class="card-h">
                        <h2>{t("cluster.my.clusterTitle")}</h2>
                        <span class="meta">{t("cluster.my.clusterMeta")}</span>
                      </div>
                      <div class="card-b">
                        <dl class="kv" style={{ "margin-top": 0 }}>
                          <dt>{t("cluster.kpi.members")}</dt>
                          <dd>{v.memberCount}</dd>
                          <dt>{t("cluster.kpi.shared")}</dt>
                          <dd class="num">{kwh(v.shared)}</dd>
                          <dt>{t("cluster.kpi.selfSufficiency")}</dt>
                          <dd class="num">
                            {formatShare(v.selfSufficiency, locale())}
                          </dd>
                          <dt>{t("cluster.kpi.selfConsumption")}</dt>
                          <dd class="num">
                            {formatShare(v.selfConsumption, locale())}
                          </dd>
                        </dl>
                        <Show when={v.completeHours < v.hours}>
                          <p class="hint" style={{ margin: 0 }}>
                            {t("cluster.incomplete", {
                              complete: v.completeHours,
                              hours: v.hours,
                            })}
                          </p>
                        </Show>
                      </div>
                    </section>
                  </>
                )}
              </For>
            </Show>
          )}
        </Show>
      </Show>
    </>
  );
}

function ClusterDialog(props: {
  cluster?: Cluster;
  onClose: () => void;
  onSaved: (c: Cluster) => void;
}) {
  const [name, setName] = createSignal(props.cluster?.name ?? "");
  const [run, error, busy] = useAction();
  return (
    <Dialog
      title={props.cluster ? t("cluster.rename") : t("cluster.new")}
      onClose={props.onClose}
      footer={
        <>
          <button type="button" class="btn" onClick={() => props.onClose()}>
            {t("common.cancel")}
          </button>
          <button
            type="button"
            class="btn primary"
            disabled={busy() || !name().trim()}
            onClick={() =>
              void run(async () => {
                const saved = await api.clusters.save(
                  props.cluster?.id ?? null,
                  name().trim(),
                );
                props.onSaved(saved);
                props.onClose();
              })
            }
          >
            {t("common.save")}
          </button>
        </>
      }
    >
      <div class="field">
        <label for="clName">{t("cluster.name")}</label>
        <input
          id="clName"
          value={name()}
          placeholder={t("cluster.namePlaceholder")}
          onInput={(e) => setName(e.currentTarget.value)}
        />
      </div>
      <Show when={error()}>
        <ErrorBox error={error()} />
      </Show>
    </Dialog>
  );
}

function MemberDialog(props: {
  cluster: Cluster;
  member?: ClusterMember;
  taken: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const m = props.member;
  // Places with an electricity grid connection meter (main point), not yet in the cluster.
  const places = () => {
    const tree = siteTree();
    const withMain = new Set(
      (tree?.points ?? [])
        .filter((p) => p.role === "main" && p.medium === "el")
        .map((p) => p.nodeId),
    );
    return (tree?.nodes ?? []).filter(
      (n) =>
        withMain.has(n.id) &&
        (n.id === m?.nodeId || !props.taken.includes(n.id)),
    );
  };
  const today = new Date().toISOString().slice(0, 10);
  const [node, setNode] = createSignal(m?.nodeId ?? places()[0]?.id ?? "");
  const [weight, setWeight] = createSignal(String(m?.weight ?? 1));
  const [joined, setJoined] = createSignal(m?.joinedOn ?? today);
  const [left, setLeft] = createSignal(m?.leftOn ?? "");
  const [run, error, busy] = useAction();
  return (
    <Dialog
      title={
        m
          ? t("cluster.editMember", { name: placeName(m.nodeId) })
          : t("cluster.addMember")
      }
      onClose={props.onClose}
      footer={
        <>
          <button type="button" class="btn" onClick={() => props.onClose()}>
            {t("common.cancel")}
          </button>
          <button
            type="button"
            class="btn primary"
            disabled={busy() || !node() || !joined()}
            onClick={() =>
              void run(async () => {
                await api.clusters.saveMember(props.cluster.id, m?.id ?? null, {
                  nodeId: node(),
                  weight: Number(weight().replace(",", ".")),
                  joinedOn: joined(),
                  leftOn: left() || null,
                });
                props.onSaved();
                props.onClose();
              })
            }
          >
            {t("common.save")}
          </button>
        </>
      }
    >
      <div style={{ display: "grid", gap: "10px" }}>
        <div class="field">
          <label for="clNode">{t("cluster.place")}</label>
          <select
            id="clNode"
            value={node()}
            onChange={(e) => setNode(e.currentTarget.value)}
          >
            <For each={places()}>
              {(n) => <option value={n.id}>{placeName(n.id)}</option>}
            </For>
          </select>
          <span class="hint">{t("cluster.placeHint")}</span>
        </div>
        <div class="grid g-2e" style={{ gap: "10px" }}>
          <div class="field">
            <label for="clWeight">{t("cluster.weight")}</label>
            <input
              id="clWeight"
              inputmode="decimal"
              value={weight()}
              onInput={(e) => setWeight(e.currentTarget.value)}
            />
            <span class="hint">{t("cluster.weightHint")}</span>
          </div>
          <div class="field">
            <label for="clJoined">{t("cluster.joinedOn")}</label>
            <input
              id="clJoined"
              type="date"
              value={joined()}
              onInput={(e) => setJoined(e.currentTarget.value)}
            />
          </div>
          <div class="field">
            <label for="clLeft">{t("cluster.leftOn")}</label>
            <input
              id="clLeft"
              type="date"
              value={left()}
              onInput={(e) => setLeft(e.currentTarget.value)}
            />
          </div>
        </div>
        <Show when={error()}>
          <ErrorBox error={error()} />
        </Show>
      </div>
    </Dialog>
  );
}
